import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Shield, ShieldCheck, Upload, FileText, Clock, CheckCircle2, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useTranslation } from '../i18n';

const TIER_CONFIG = {
  0: { name: 'Unverified', color: 'bg-muted text-muted-foreground', icon: Shield, description: 'Your business has not been verified yet.' },
  1: { name: 'Verified', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: ShieldCheck, description: 'Business credentials reviewed by PocketFiler.' },
  2: { name: 'Premium Verified', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200', icon: ShieldCheck, description: 'Deep verification with enhanced trust signals.' }
};

export default function VerificationTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedTier, setSelectedTier] = useState(1);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState('');

  // Fetch verification status
  const { data: status, isLoading } = useQuery({
    queryKey: ['verification-status'],
    queryFn: () => api.get('/verification/status').then(r => r.data)
  });

  // Fetch document types
  const { data: docTypes } = useQuery({
    queryKey: ['verification-doc-types'],
    queryFn: () => api.get('/verification/document-types').then(r => r.data)
  });

  // Submit verification request
  const submitMutation = useMutation({
    mutationFn: (data) => api.post('/verification/request', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['verification-status']);
      setDocuments([]);
      setNotes('');
    }
  });

  // Cancel pending request
  const cancelMutation = useMutation({
    mutationFn: () => api.delete('/verification/request'),
    onSuccess: () => queryClient.invalidateQueries(['verification-status'])
  });

  // Upload a document
  const handleFileUpload = useCallback(async (file, docType) => {
    setUploading(true);
    try {
      // Get presigned URL
      const { data } = await api.post('/verification/documents/presigned', {
        file_name: file.name,
        content_type: file.type || 'application/pdf'
      });

      // Upload to S3
      await fetch(data.upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/pdf' }
      });

      setDocuments(prev => [...prev, {
        document_type: docType,
        file_url: data.file_url,
        file_name: file.name
      }]);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleSubmit = () => {
    submitMutation.mutate({
      tier_requested: selectedTier,
      documents,
      notes: notes || undefined
    });
  };

  const removeDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const currentTier = status?.current_tier || 0;
  const tierInfo = TIER_CONFIG[currentTier];
  const TierIcon = tierInfo.icon;
  const hasPending = !!status?.pending_request;
  const lastReview = status?.last_review;
  const requirements = docTypes?.tier_requirements || {};

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TierIcon className="h-5 w-5" />
            {t("verification.status") || "Verification Status"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <Badge className={tierInfo.color + ' text-sm px-3 py-1'}>
              {tierInfo.name}
            </Badge>
            {status?.expires_at && (
              <span className="text-sm text-muted-foreground">
                Expires: {new Date(status.expires_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{tierInfo.description}</p>

          {/* Last review result */}
          {lastReview && lastReview.status === 'rejected' && (
            <div className="mt-4 p-3 rounded-md bg-destructive/10 border border-destructive/20">
              <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                <XCircle className="h-4 w-4" />
                Previous request was not approved
              </div>
              {lastReview.admin_notes && (
                <p className="mt-1 text-sm text-muted-foreground">{lastReview.admin_notes}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Request */}
      {hasPending && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <Clock className="h-5 w-5" />
              {t("verification.inProgress") || "Verification In Progress"}
            </CardTitle>
            <CardDescription>
              {t("verification.reviewTime") || "Your request is being reviewed. This typically takes 1-2 business days."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Tier Requested</span>
                <Badge variant="outline">
                  {TIER_CONFIG[status.pending_request.tier_requested]?.name}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Documents Submitted</span>
                <span>{status.pending_request.documents?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Submitted</span>
                <span>{new Date(status.pending_request.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isLoading}
            >
              {t("verification.cancelRequest") || "Cancel Request"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Apply for Verification */}
      {!hasPending && currentTier < 2 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("verification.getVerified") || "Get Verified"}</CardTitle>
            <CardDescription>
              Upload your business documents to earn a verification badge. Verified businesses
              appear higher in search results and build more trust with buyers.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Tier Selection */}
            <div className="space-y-2">
              <Label>{t("verification.level") || "Verification Level"}</Label>
              <Select
                value={String(selectedTier)}
                onValueChange={(v) => setSelectedTier(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currentTier < 1 && (
                    <SelectItem value="1">
                      Verified — Business credentials review (2 documents minimum)
                    </SelectItem>
                  )}
                  <SelectItem value="2">
                    Premium Verified — Deep verification (4 documents minimum)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Requirements */}
            {requirements[selectedTier] && (
              <div className="p-3 rounded-md bg-muted/50 text-sm space-y-1">
                <p className="font-medium">Requirements for {TIER_CONFIG[selectedTier].name}:</p>
                <p>Minimum {requirements[selectedTier].min_documents} documents</p>
                <p>Required: {requirements[selectedTier].required_types?.map(t =>
                  docTypes?.document_types?.find(dt => dt.id === t)?.label || t
                ).join(', ')}</p>
              </div>
            )}

            {/* Document Upload */}
            <div className="space-y-3">
              <Label>{t("verification.uploadDocs") || "Upload Documents"}</Label>
              {documents.map((doc, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-md border bg-card">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{doc.file_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {docTypes?.document_types?.find(dt => dt.id === doc.document_type)?.label || doc.document_type}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeDocument(i)}>
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              <DocumentUploader
                documentTypes={docTypes?.document_types || []}
                onUpload={handleFileUpload}
                uploading={uploading}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{t("verification.additionalNotes") || "Additional Notes (optional)"}</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional context about your business..."
                rows={3}
              />
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={documents.length === 0 || submitMutation.isLoading}
              className="w-full"
            >
              {submitMutation.isLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("verification.submitting") || "Submitting..."}</>
              ) : (
                <><ShieldCheck className="h-4 w-4 mr-2" /> {t("verification.submit") || "Submit for Verification"}</>
              )}
            </Button>

            {submitMutation.isError && (
              <div className="p-3 rounded-md bg-destructive/10 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                {submitMutation.error?.response?.data?.detail || 'Failed to submit. Please try again.'}
              </div>
            )}

            {submitMutation.isSuccess && (
              <div className="p-3 rounded-md bg-green-50 dark:bg-green-900/20 text-sm text-green-700 dark:text-green-300">
                <CheckCircle2 className="h-4 w-4 inline mr-1" />
                Verification request submitted. We'll review it within 1-2 business days.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Already at max tier */}
      {currentTier >= 2 && !hasPending && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-amber-500" />
              <div>
                <p className="font-medium">Premium Verified</p>
                <p className="text-sm text-muted-foreground">
                  Your business has the highest level of verification.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Sub-component: Document upload with type selector
function DocumentUploader({ documentTypes, onUpload, uploading }) {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState('');
  const fileInputRef = React.useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && selectedType) {
      onUpload(file, selectedType);
      e.target.value = '';
    }
  };

  return (
    <div className="flex gap-2">
      <Select value={selectedType} onValueChange={setSelectedType}>
        <SelectTrigger className="flex-1">
          <SelectValue placeholder={t("verification.selectDocType") || "Select document type..."} />
        </SelectTrigger>
        <SelectContent>
          {documentTypes.map(dt => (
            <SelectItem key={dt.id} value={dt.id}>{dt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        onChange={handleFileSelect}
        className="hidden"
      />
      <Button
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        disabled={!selectedType || uploading}
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      </Button>
    </div>
  );
}
